if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form =
          this.querySelector('form[data-type="add-to-cart-form"]') ||
          this.querySelector("form");

        this.formInput = this.form?.querySelector('[name="id"]');

        this.submitButton = this.querySelector('[name="add"]');

        this.submitButtonText = this.submitButton?.querySelector("span");

        this.loader = this.submitButton?.querySelector(
          ".loading-overlay__spinner",
        );

        this.cart = document.querySelector("cart-drawer");

        this.cartType = this.cart?.dataset.cartType || "page";

        this.hideErrors = this.dataset.hideErrors === "true";

        this.onSubmitHandler = this.onSubmitHandler.bind(this);

        if (!this.form || !this.formInput || !this.submitButton) {
          console.error(
            "[Ashpveda product form] Required form elements are missing.",
          );
          return;
        }

        this.formInput.disabled = false;

        this.form.addEventListener("submit", this.onSubmitHandler);

        if (this.cartType === "drawer") {
          this.submitButton.setAttribute("aria-haspopup", "dialog");
        }
      }

      disconnectedCallback() {
        this.form?.removeEventListener("submit", this.onSubmitHandler);
      }

      getSectionsToRender() {
        if (!this.cart || typeof this.cart.getSectionsToRender !== "function") {
          return [];
        }

        return this.cart
          .getSectionsToRender()
          .map((section) => {
            /*
             * Ashpveda cart objects can use:
             * section.section = Shopify section handle
             * section.id = DOM target ID
             */
            return section.section || section.id;
          })
          .filter(Boolean);
      }

      async onSubmitHandler(event) {
        event.preventDefault();

        if (
          !this.form ||
          !this.submitButton ||
          this.submitButton.disabled ||
          this.submitButton.getAttribute("aria-disabled") === "true"
        ) {
          return;
        }

        this.handleErrorMessage();
        this.setLoading(true);

        this.cart?.setActiveElement?.(document.activeElement);

        const config = fetchConfig("javascript");

        config.headers["X-Requested-With"] = "XMLHttpRequest";

        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);
        const sections = this.getSectionsToRender();

        if (sections.length) {
          formData.append("sections", sections.join(","));

          formData.append("sections_url", window.location.pathname);
        }

        config.body = formData;

        try {
          const fetchResponse = await fetch(
            window.routes?.cart_add_url || "/cart/add.js",
            config,
          );

          const response = await fetchResponse.json();

          if (!fetchResponse.ok || response.status || response.errors) {
            const message =
              response.description ||
              response.message ||
              window.cartStrings?.error ||
              "Unable to add this product to cart.";

            this.handleErrorMessage(message);

            if (
              typeof publish === "function" &&
              typeof PUB_SUB_EVENTS !== "undefined" &&
              PUB_SUB_EVENTS.cartError
            ) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || message,
                message,
              });
            }

            return;
          }

          if (
            typeof publish === "function" &&
            typeof PUB_SUB_EVENTS !== "undefined" &&
            PUB_SUB_EVENTS.cartUpdate
          ) {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: "product-form",
              productVariantId: formData.get("id"),
              cartData: response,
            });
          }

          /*
           * Refresh independent product-page cart features,
           * including the free-shipping progress bar.
           */
          window.dispatchEvent(
            new CustomEvent("ashpveda:cart-updated", {
              detail: {
                source: "product-form",
                productVariantId: formData.get("id"),
              },
            }),
          );

          const quickAddModal = this.closest("quick-add-modal");

          if (quickAddModal) {
            document.body.addEventListener(
              "modalClosed",
              () => {
                window.setTimeout(() => {
                  this.showCart(response);
                }, 0);
              },
              { once: true },
            );

            if (this.cartType === "drawer") {
              quickAddModal.hide(true);
            } else {
              this.showCart(response);
            }

            return;
          }

          this.showCart(response);
        } catch (error) {
          console.error("[Ashpveda Add to Cart]", error);

          this.handleErrorMessage(
            window.cartStrings?.error ||
              "Something went wrong. Please try again.",
          );
        } finally {
          this.setLoading(false);
          this.cleanupQuickBuy();
        }
      }

      showCart(response) {
        if (
          this.cartType === "drawer" &&
          this.cart &&
          typeof this.cart.renderContents === "function"
        ) {
          try {
            const isClosedCart = !document.body.classList.contains(
              "page-overlay-cart-on",
            );

            this.cart.classList.remove("wt-cart--empty", "is-empty");

            this.cart.renderContents(response, isClosedCart);

            return;
          } catch (renderError) {
            console.error("[Ashpveda cart drawer render]", renderError);
          }
        }

        window.location.assign(window.routes?.cart_url || "/cart");
      }

      setLoading(isLoading) {
        if (!this.submitButton) return;

        this.submitButton.classList.toggle("loading", isLoading);

        this.loader?.classList.toggle("hidden", !isLoading);

        if (isLoading) {
          this.submitButton.setAttribute("aria-disabled", "true");
        } else if (!this.submitButton.disabled) {
          this.submitButton.removeAttribute("aria-disabled");
        }
      }

      cleanupQuickBuy() {
        const container = document.querySelector(".wt__quick-buy__container");

        if (
          !container ||
          container.classList.contains("hidden") ||
          !container.classList.contains("wt__quick-buy__container--open")
        ) {
          return;
        }

        const overlay = document.querySelector(".wt__quick-buy--page-overlay");

        const product = container.querySelector(".wt-product");

        const loader = container.querySelector(".wt__quick-buy-loader");

        product?.remove();

        container.classList.remove("wt__quick-buy__container--open");

        overlay?.classList.remove("wt__quick-buy--page-overlay--open");

        document.body.classList.remove("quick-buy-page-overlay");

        loader?.classList.remove("hidden");
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(
            ".product-form__error-message-wrapper[role='alert']",
          );

        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message",
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage && this.errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    },
  );
}
